import {
  deliveryFeeDisplay,
  selectedDeliveryTotals,
} from 'src/utils/deliveryFeeDisplay'
import useDeliveryFeePreview from 'src/utils/useDeliveryFeePreview'
import { Table, Tag, notification } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import NoDataScreen from 'src/components/shared/no-data-screen/NoDataScreen'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import useIsBlocked from 'src/hooks/useIsBlocked'
import styled from 'styled-components/macro'
import _ from 'underscore'
import BasketTableColumn from './BasketTableColumn'
import { isOrderableBasketLine } from 'src/utils/basketLines'
import { countBasketLinesAndUnits } from 'src/utils/orderLineCounts'
import {
  createOrder,
  fetchOrders,
  setBasket,
} from '../../../features/OrdersSlice'

const Basket = ({ venueId }) => {
  const history = useHistory()
  const basketLines = useSelector(state => state.orders.basket)
  const orders = useMemo(
    () =>
      (Array.isArray(basketLines) ? basketLines : []).filter(
        isOrderableBasketLine,
      ),
    [basketLines],
  )
  const venueSuppliers = useSelector(
    state => state.venueSuppliers.venueSuppliers,
  )
  const isLoading = useSelector(state => state.orders.isLoading)
  const venue = useSelector(state => state.venue.venue)
  const [removeProductId, setRemoveProductId] = useState('')
  const updateLoading = useSelector(state => state.orders.isUpdateLoading)
  const dispatch = useDispatch()
  const isPricesHidden = venue.isPricesHidden || false
  useEffect(() => {
    Promise.resolve(dispatch(fetchOrders(venueId))).catch(() => {})
  }, [dispatch, venueId])

  const preview = useDeliveryFeePreview(
    venueId,
    orders,
    isLoading || updateLoading,
    venueSuppliers,
  )
  const totals = selectedDeliveryTotals(preview.summary?.suppliers)
  const goodsIncludingVAT =
    totals.goods +
    (preview.summary?.suppliers || [])
      .flatMap(supplier => supplier.items)
      .reduce((vat, item) => vat + (item.hasVat ? item.total * 0.2 : 0), 0)
  const blockedSuppliers = (preview.summary?.suppliers || []).filter(
    s => !deliveryFeeDisplay(s).canOrder,
  )

  const handleRemoveProduct = product => {
    setRemoveProductId(product._id)
    dispatch(
      createOrder({
        ...product,
        quantity: 0,
      }),
    )
  }

  const updateOrder = order => {
    dispatch(
      createOrder({
        ...order,
        quantity: parseFloat(order.quantity),
        method: 'update',
      }),
    )
  }

  const onChangeQuantity = (value, product) => {
    dispatch(
      setBasket(
        orders.map(prod => {
          if (prod._id === product._id) {
            const newObj = {
              ...prod,
              quantity: value,
            }
            updateOrder(newObj)
            return newObj
          } else return prod
        }),
      ),
    )
  }

  const { lines: productLineCount, units: productUnitCount } =
    countBasketLinesAndUnits(orders)

  const suppliersGrouped = _(orders).groupBy(
    order => order?.product?.supplier?._id,
  )

  const notMinimumNotification = () => {
    notification.error({
      message: 'Minimum order not met',
      description: blockedSuppliers
        .map(s => `${s.supplierTitle}: ${deliveryFeeDisplay(s).message}`)
        .join(' '),
      placement: 'RadiusBottomrightOutlined',
      bottom: 100,
    })
  }

  return (
    <Wrapper>
      {isLoading ? (
        <Loading />
      ) : !Array.isArray(orders) || orders.length < 1 ? (
        <NoDataScreen page='basket' />
      ) : (
        Object.entries(suppliersGrouped).map(([key, groupedOrders]) => {
          const venueSupplier = venueSuppliers?.find(
            supp =>
              supp?.venue === venueId &&
              supp?.supplier?._id === groupedOrders[0]?.product?.supplier?._id,
          )
          const isRestricted = venueSupplier?.restricted === true
          const supplierPreview = preview.summary?.suppliers.find(
            s => s.supplierId === key,
          )
          const amounts = supplierPreview
            ? deliveryFeeDisplay(supplierPreview)
            : null
          return (
            <SupplierTable key={key}>
              <TableHeader>
                <h3>
                  {groupedOrders[0]?.product?.supplier?.title}{' '}
                  {isRestricted && (
                    <Tag color='orange' style={{ marginLeft: 8 }}>
                      Restricted – manager approval required
                    </Tag>
                  )}
                </h3>
                <div>
                  {!amounts ? (
                    <span>
                      {preview.error
                        ? 'Refresh basket to load totals.'
                        : 'Updating totals…'}
                    </span>
                  ) : (
                    <>
                      {!isPricesHidden && (
                        <>
                          <div>Goods (ex VAT): £{amounts.goods.toFixed(2)}</div>
                          <div>
                            <strong>
                              Delivery (ex VAT): £{amounts.delivery.toFixed(2)}
                            </strong>
                          </div>
                          <h3>
                            Order total (ex VAT): £{amounts.total.toFixed(2)}
                          </h3>
                        </>
                      )}
                      {amounts.message && (
                        <p role={!amounts.canOrder ? 'alert' : undefined}>
                          {amounts.message}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </TableHeader>
              <Table
                bordered
                style={{
                  background: 'white',
                }}
                pagination={false}
                dataSource={groupedOrders.map((prod, index) => ({
                  key: prod._id,
                  index: index,
                  ...prod,
                }))}
                columns={BasketTableColumn(
                  orders,
                  handleRemoveProduct,
                  onChangeQuantity,
                  updateLoading,
                  removeProductId,
                  isPricesHidden,
                )}
              />
            </SupplierTable>
          )
        })
      )}
      {isLoading || orders.length === 0 ? null : (
        <TableFooter>
          <FooterTotalContainer>
            {!isPricesHidden && (
              <>
                <FooterText>
                  {preview.loading
                    ? 'Updating totals…'
                    : preview.error
                    ? 'Could not load totals'
                    : `Order total (ex VAT): £${totals.total.toFixed(2)}`}
                </FooterText>
                {preview.summary && (
                  <>
                    <FooterTextSecondary>
                      Goods (inc VAT): £{goodsIncludingVAT.toFixed(2)}
                    </FooterTextSecondary>
                    <FooterTextSecondary>
                      Goods £{totals.goods.toFixed(2)} + Delivery £
                      {totals.delivery.toFixed(2)} (ex VAT)
                    </FooterTextSecondary>
                  </>
                )}
              </>
            )}
          </FooterTotalContainer>
          <FooterText>
            Suppliers: {Object.keys(suppliersGrouped).length}
          </FooterText>
          <FooterText>Products: {productLineCount}</FooterText>
          <FooterText>Units: {productUnitCount}</FooterText>
          {preview.error && (
            <StyledButton
              onClick={() => {
                dispatch(fetchOrders(venueId))
                preview.retry()
              }}
            >
              Refresh
            </StyledButton>
          )}
          <StyledButton
            disabled={preview.loading || preview.error || !preview.summary}
            onClick={() => {
              if (blockedSuppliers.length > 0) {
                notMinimumNotification()
              } else {
                history.push(`/v/${venueId}/checkout`)
              }
            }}
          >
            Checkout
          </StyledButton>
        </TableFooter>
      )}
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  padding-bottom: 160px;
`
const TableFooter = styled.div`
  width: 100%;
  min-height: 80px;
  gap: 16px;
  flex-wrap: wrap;
  position: fixed;
  left: 0;
  bottom: 0;
  background: ${({ theme }) => theme.colours.avocadoGreen};
  padding: 12px 100px;
  @media (max-width: 1000px) {
    padding: 12px 16px;
  }
  display: flex;
  justify-content: space-between;
  align-items: center;
`
const FooterText = styled.h3`
  color: ${({ theme }) => theme.colours.mayoWhite};
  margin: 0;
`

const FooterTotalContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const FooterTextSecondary = styled.div`
  color: ${({ theme }) => theme.colours.mayoWhite};
  font-size: 14px;
  opacity: 0.85;
  margin: 0;
`
const StyledButton = styled.button`
  color: ${({ theme }) => theme.colours.mayoWhite};
  background-color: ${({ theme }) => theme.colours.avocadoGreen};
  border: 1px solid ${({ theme }) => theme.colours.mayoWhite};
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 18px;
  font-weight: 700;

  &:hover {
    color: ${({ theme }) => theme.colours.seaweedGreen};
    border-color: ${({ theme }) => theme.colours.seaweedGreen};
    cursor: pointer;
  }

  &:disabled {
    color: ${({ theme }) => theme.colours.oliveGrey};
    border-color: ${({ theme }) => theme.colours.oliveGrey};
    cursor: pointer;
  }
`

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`
const SupplierTable = styled.div`
  margin-bottom: 40px;
  overflow-x: auto;
`

const GatedBasket = ({ venueId }) => {
  const blocked = useIsBlocked('basket')
  if (blocked) return <PermissionBlockScreen />
  return <Basket venueId={venueId} />
}

export default GatedBasket
